import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useGreeting } from "../lib/greeting";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { AlertRow } from "../components/StatusBadge";
import { money, monthLabel, addMonth, fyLabel, DEMO_MONTH } from "../lib/format";
import { ChevronLeft, ChevronRight, TrendingUp, Wallet, Clock, Receipt, PiggyBank, Landmark, FileText, Building2 } from "lucide-react";
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from "recharts";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const greeting = useGreeting(user);
  const [month, setMonth] = useState(DEMO_MONTH);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", month],
    queryFn: async () => (await api.get(`/dashboard?month=${month}`)).data,
  });

  const { data: trends } = useQuery({
    queryKey: ["trends", month],
    queryFn: async () => (await api.get(`/trends?end=${month}&months=6`)).data,
  });

  const qc = useQueryClient();
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reservePct, setReservePct] = useState("30");
  const openReserve = () => { setReservePct(String((data?.tax_reserve_bps ?? 3000) / 100)); setReserveOpen(true); };
  const saveReserve = async () => {
    await api.put("/settings", { tax_reserve_bps: Math.round((parseFloat(reservePct) || 0) * 100) });
    qc.invalidateQueries({ queryKey: ["dashboard", month] });
    setReserveOpen(false);
    toast.success("Tax reserve updated");
  };
  const [superOpen, setSuperOpen] = useState(false);
  const [superPct, setSuperPct] = useState("12");
  const openSuper = () => { setSuperPct(String((data?.super_bps ?? 1200) / 100)); setSuperOpen(true); };
  const saveSuper = async () => {
    await api.put("/settings", { super_bps: Math.round((parseFloat(superPct) || 0) * 100) });
    qc.invalidateQueries({ queryKey: ["dashboard", month] });
    setSuperOpen(false);
    toast.success("Super set-aside updated");
  };

  const isNew = !isLoading && (data?.practice_count ?? 0) === 0;

  const metrics = [
    { key: "expected", label: "Expected earnings", value: data?.expected_cents, icon: TrendingUp,
      tone: "text-slate-900", ring: "bg-[#E6F4F1] text-[#0D786A]", testId: "metric-expected-earnings" },
    { key: "received", label: "Received", value: data?.received_cents, icon: Wallet,
      tone: "text-emerald-700", ring: "bg-emerald-50 text-emerald-600", testId: "metric-received-amount" },
    { key: "still", label: "Still to receive", value: data?.outstanding_cents, icon: Clock,
      tone: "text-indigo-700", ring: "bg-indigo-50 text-indigo-600", testId: "metric-still-to-receive" },
  ];

  return (
    <AppShell>
      <div className="px-5 lg:px-8 pt-7 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold tracking-wider uppercase text-[#0D786A]">{fyLabel(month)}</div>
          <h1 data-testid="dashboard-greeting" className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{greeting}</h1>
          <p data-testid="dashboard-subtext" className="text-sm text-slate-500 mt-0.5">Here's where your money stands today.</p>
        </div>
        <button data-testid="home-accountant-shortcut" onClick={() => navigate("/more/accountant")}
          className="mt-1 h-10 w-10 shrink-0 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#0D786A] df-pressable">
          <FileText size={18} />
        </button>
      </div>

      {/* Month navigation */}
      {isNew ? (
        <div className="px-5 mt-6 df-fade-in" data-testid="onboarding-view">
          <div className="df-card p-7 text-center">
            <img src="/molaroo-mark.png" alt="Molaroo" className="mx-auto h-24 w-24 rounded-2xl object-contain bg-white shadow-sm mb-4" />
            <h2 className="font-heading font-bold text-xl text-slate-900">Welcome to Molaroo</h2>
            <p className="text-sm text-slate-500 mt-1.5">Let's set up your income tracking in two quick steps.</p>
            <p className="text-[11px] text-slate-400 mt-1">by NavScora</p>
          </div>
          <div className="mt-4 space-y-3">
            <div className="df-card p-5 flex items-start gap-3">
              <span className="h-8 w-8 rounded-full bg-[#E6F4F1] text-[#0D786A] font-bold flex items-center justify-center shrink-0">1</span>
              <div className="flex-1">
                <div className="font-heading font-semibold text-slate-900">Add your first practice</div>
                <p className="text-sm text-slate-500 mt-0.5 mb-3">Set up where you work and how you're paid — percentage, hourly, daily or a fixed rate.</p>
                <Button data-testid="onboarding-add-practice" onClick={() => navigate("/practices/add")}
                  className="w-full h-11 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
                  <Building2 size={18} className="mr-1.5" /> Add your first practice
                </Button>
              </div>
            </div>
            <div className="df-card p-5 flex items-start gap-3">
              <span className="h-8 w-8 rounded-full bg-slate-100 text-slate-400 font-bold flex items-center justify-center shrink-0">2</span>
              <div className="flex-1">
                <div className="font-heading font-semibold text-slate-900">Log your first income</div>
                <p className="text-sm text-slate-500 mt-0.5">Once a practice is added, record a work day and Molaroo calculates your expected earnings automatically.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (<>
      <div className="px-5 mt-2">
        <div className="df-card flex items-center justify-between px-2 py-2">
          <button data-testid="month-prev" onClick={() => setMonth(addMonth(month, -1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <span data-testid="current-month" className="font-heading font-semibold text-slate-900">{monthLabel(month)}</span>
          <button data-testid="month-next" onClick={() => setMonth(addMonth(month, 1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      {/* Hero metrics */}
      <div className="px-5 mt-4 space-y-3 df-fade-in">
        <div className="df-card p-5" data-testid={metrics[0].testId}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">{metrics[0].label}</span>
            <span className={`h-9 w-9 rounded-full flex items-center justify-center ${metrics[0].ring}`}>
              <TrendingUp size={18} />
            </span>
          </div>
          <div className="mt-2 font-num font-bold text-4xl tracking-tight text-slate-900">
            {isLoading ? "—" : money(data?.expected_cents)}
          </div>
        </div>
        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
          {metrics.slice(1).map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.key} className="df-card p-4 min-w-0" data-testid={m.testId}>
                <span className={`h-8 w-8 rounded-full flex items-center justify-center ${m.ring}`}>
                  <Icon size={16} />
                </span>
                <div className="mt-3 text-xs font-medium text-slate-500">{m.label}</div>
                <div className={`font-num font-bold text-xl tracking-tight tabular-nums whitespace-nowrap ${m.tone}`}>
                  {isLoading ? "—" : money(m.value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Income trend */}
      <div className="px-5 mt-6">
        <button onClick={() => navigate("/money")} data-testid="home-reports-card"
          className="w-full df-card p-4 flex items-center justify-between df-pressable mb-6">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><FileText size={18} /></span>
            <div className="text-left">
              <div className="font-heading font-semibold text-slate-900">Payment reports</div>
              <div className="text-xs text-slate-500">{(data?.report_count ?? 0)} this month · tap to record or review</div>
            </div>
          </div>
          <ChevronRight size={20} className="text-slate-300" />
        </button>
      </div>

      {/* Income trend */}
      <div className="px-5 mt-6">
        <div className="df-card p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold tracking-tight text-slate-900">Income trend</h2>
            <span className="text-xs text-slate-400">last 6 months</span>
          </div>
          <div className="h-40 -ml-2" data-testid="income-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={(trends || []).map((t) => ({ name: t.label, Expected: t.expected_cents / 100, Received: t.received_cents / 100, Expenses: t.expenses_cents / 100 }))}>
                <defs>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0D786A" stopOpacity={0.25} /><stop offset="100%" stopColor="#0D786A" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4F46E5" stopOpacity={0.2} /><stop offset="100%" stopColor="#4F46E5" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gCost" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D97706" stopOpacity={0.18} /><stop offset="100%" stopColor="#D97706" stopOpacity={0} /></linearGradient>
                </defs>
                <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString("en-AU")}`} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Area type="monotone" dataKey="Expected" stroke="#0D786A" strokeWidth={2} fill="url(#gExp)" />
                <Area type="monotone" dataKey="Received" stroke="#4F46E5" strokeWidth={2} fill="url(#gRec)" />
                <Area type="monotone" dataKey="Expenses" stroke="#D97706" strokeWidth={2} fill="url(#gCost)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#0D786A]" />Expected</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#4F46E5]" />Received</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#D97706]" />Expenses</span>
          </div>
        </div>
      </div>

      {/* Needs attention */}
      <div className="px-5 mt-7">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-3">Needs Attention</h2>
        {isLoading ? null : data?.needs_attention?.length ? (
          <div className="space-y-2.5" data-testid="needs-attention-list">
            {data.needs_attention.map((a, i) => (
              <AlertRow key={i} alert={a}
                onClick={() => a.report_id
                  ? navigate(`/money/report/${a.report_id}`)
                  : navigate(`/practices/${a.practice_id}/check?start=${month}-01&end=${month}-31`)} />
            ))}
          </div>
        ) : (
          <div className="df-card p-6 text-center">
            <div className="font-heading font-semibold text-slate-900">You're all caught up</div>
            <p className="text-sm text-slate-500 mt-1">No outstanding payments this month.</p>
          </div>
        )}
      </div>

      {/* Expenses & more */}
      <div className="px-5 mt-7">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-3">Expenses & more</h2>
        <div className="grid grid-cols-1 min-[430px]:grid-cols-3 gap-3">
          <button onClick={() => navigate("/money/expenses")} data-testid="home-expenses-card"
            className="df-card p-4 df-pressable min-w-0 flex items-center gap-3 text-left min-[430px]:flex-col min-[430px]:items-center min-[430px]:text-center min-[430px]:gap-0">
            <span className="h-9 w-9 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0"><Receipt size={17} /></span>
            <div className="min-w-0 min-[430px]:mt-2">
              <div className="text-xs font-semibold text-slate-700">Expenses</div>
              <div className="font-num font-bold text-sm text-slate-900 tabular-nums whitespace-nowrap">{isLoading ? "—" : money(data?.expenses_cents)}</div>
            </div>
          </button>
          <button onClick={openReserve} data-testid="home-tax-reserve-card"
            className="df-card p-4 df-pressable min-w-0 flex items-center gap-3 text-left min-[430px]:flex-col min-[430px]:items-center min-[430px]:text-center min-[430px]:gap-0">
            <span className="h-9 w-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Landmark size={17} /></span>
            <div className="min-w-0 min-[430px]:mt-2">
              <div className="text-xs font-semibold text-slate-700">Tax Reserve</div>
              <div className="font-num font-bold text-sm text-slate-900 tabular-nums whitespace-nowrap">{isLoading ? "—" : money(data?.suggested_tax_reserve_cents)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{(data?.tax_reserve_bps ?? 3000) / 100}% set aside</div>
            </div>
          </button>
          <button onClick={openSuper} data-testid="home-super-card"
            className="df-card p-4 df-pressable min-w-0 flex items-center gap-3 text-left min-[430px]:flex-col min-[430px]:items-center min-[430px]:text-center min-[430px]:gap-0">
            <span className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><PiggyBank size={17} /></span>
            <div className="min-w-0 min-[430px]:mt-2">
              <div className="text-xs font-semibold text-slate-700">Super</div>
              <div className="font-num font-bold text-sm text-slate-900 tabular-nums whitespace-nowrap">{isLoading ? "—" : money(data?.suggested_super_cents)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{(data?.super_bps ?? 1200) / 100}% set aside</div>
            </div>
          </button>
        </div>
      </div>
      </>)}

      <Dialog open={reserveOpen} onOpenChange={setReserveOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Tax reserve</DialogTitle>
            <DialogDescription>Choose a percentage to set aside from money you receive. A personal estimate — not tax advice.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" step="1" inputMode="decimal" data-testid="reserve-input" value={reservePct}
              onChange={(e) => setReservePct(e.target.value)} className="h-11 rounded-xl font-num" />
            <span className="text-slate-500">%</span>
          </div>
          <div className="mt-2 text-sm text-slate-600">Suggested from this month's received:{" "}
            <b className="font-num">{money(Math.round((data?.received_cents || 0) * (parseFloat(reservePct) || 0) / 100))}</b>
          </div>
          <button data-testid="reserve-manage-link" onClick={() => { setReserveOpen(false); navigate("/more/reserves"); }}
            className="mt-2 text-sm font-semibold text-[#0D786A] df-pressable">Track reserved vs suggested →</button>
        </DialogContent>
      </Dialog>

      <Dialog open={superOpen} onOpenChange={setSuperOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Super set-aside</DialogTitle>
            <DialogDescription>Choose a percentage to set aside for superannuation from money you receive. A personal estimate — not advice, and does not determine your super entitlement.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" step="0.5" inputMode="decimal" data-testid="super-input" value={superPct}
              onChange={(e) => setSuperPct(e.target.value)} className="h-11 rounded-xl font-num" />
            <span className="text-slate-500">%</span>
          </div>
          <div className="mt-2 text-sm text-slate-600">Suggested from this month's received:{" "}
            <b className="font-num">{money(Math.round((data?.received_cents || 0) * (parseFloat(superPct) || 0) / 100))}</b>
          </div>
          <button data-testid="super-manage-link" onClick={() => { setSuperOpen(false); navigate("/more/reserves"); }}
            className="mt-2 text-sm font-semibold text-[#0D786A] df-pressable">Track reserved vs suggested →</button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuperOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="super-save" onClick={saveSuper} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
